import React, { useEffect, useRef, useState } from "react";
import config from "../config";
import { useNavigate, useLocation } from "react-router-dom";
import {
  ShieldCheck,
  Star,
  Heart,
  Eye,
  CircleCheckBig,
  Handshake,
  TrendingUp,
} from "lucide-react";

//import FAQ from "./FAQ";
import { aboutFaqSchema } from "../schemas/aboutSchema";
/* ─────────────────────────────────────────────
   ANIMATED COUNTER HOOK
───────────────────────────────────────────── */
function useCountUp(target, duration = 2000, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const ease = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(ease * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [start, target, duration]);
  return count;
}

/* ─────────────────────────────────────────────
   INTERSECTION OBSERVER HOOK
───────────────────────────────────────────── */
function useInView(options = {}) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2, ...options },
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, inView];
}

/* ─────────────────────────────────────────────
   COUNTER CARD
───────────────────────────────────────────── */
function StatCard({ value, suffix, label, delay, started }) {
  const count = useCountUp(value, 2200, started);
  return (
    <div className="wcu-stat-card" style={{ transitionDelay: `${delay}ms` }}>
      <div className="wcu-stat-number">
        <span className="wcu-stat-count">{count}</span>
        <span className="wcu-stat-suffix">{suffix}</span>
      </div>
      <p className="wcu-stat-label">{label}</p>
    </div>
  );
}

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
const WhyChooseUs = () => {
  const [heroRef, heroInView] = useInView({ threshold: 0.1 });
  const [storyRef, storyInView] = useInView();
  const [statsRef, statsInView] = useInView();
  const [valuesRef, valuesInView] = useInView();
  const navigate = useNavigate();
  const location = useLocation();

  const values = [
    {
      icon: <ShieldCheck size={28} strokeWidth={1.5} />,
      title: "Integrity",
      text: "Integrity, reliability, and accountability are our three pillars of foundation.",
    },
    {
      icon: <Star size={28} strokeWidth={1.5} />,
      title: "Respect",
      text: "We earn respect through honesty, trust, and valuing individual differences, reinforcing passion in all that we do.",
    },
    {
      icon: <Heart size={28} strokeWidth={1.5} />,
      title: "Empathy",
      text: "As CFO consultants, We connect better with our partners and stakeholders through empathy. It requires the ability to connect and listen.",
    },
    {
      icon: <Eye size={28} strokeWidth={1.5} />,
      title: "Transparency",
      text: "We communicate with honesty, respect, and openness — promoting trust and enabling sharper, faster decisions across your business.",
    },
    {
      icon: <CircleCheckBig size={28} strokeWidth={1.5} />,
      title: "Accountability",
      text: "We take ownership and solve problems with good judgement, without the need for excessive policies and procedures in our cfo consulting.",
    },
    {
      icon: <Handshake size={28} strokeWidth={1.5} />,
      title: "Partnership",
      text: "We build strong relationships with our clients based on trust, ability to listen and reflect, effective collaboration, and a commitment to their success.",
    },
    {
      icon: <TrendingUp size={28} strokeWidth={1.5} />,
      title: "Add Value to Stakeholders",
      text: "Our clients are at the centre of our cfo services. We are attentive to their needs and committed to helping them succeed.",
    },
  ];

  const handleContactClick = (e) => {
    e.preventDefault();

    // If not on home page → go home first
    if (location.pathname !== "/") {
      navigate("/");

      setTimeout(() => {
        const el = document.getElementById("contact");
        if (el) {
          el.scrollIntoView({ behavior: "smooth" });
        }
      }, 300);

      return;
    }

    // If already on home → scroll directly
    const el = document.getElementById("contact");
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=Outfit:wght@300;400;500;600&display=swap');

        /* ── BRAND TOKENS ──────────────────────── */
        .wcu-root {
          --blue:       #014293;
          --blue-dark:  #012e6a;
          --blue-mid:   #01306e;
          --light:      #F0F3FD;
          --dark:       #292929;
          --gray:       #D9D9D9;
          --white:      #ffffff;
          --text-mid:   #555f72;
          --serif: 'Cormorant Garamond', Georgia, serif;
          --sans:  'Outfit', sans-serif;
          --radius:    4px;
          --radius-lg: 12px;
          font-family: var(--sans);
          color: var(--dark);
          background: var(--white);
          overflow-x: hidden;
        }

        /* ════════════════════════════════════════
           HERO
        ════════════════════════════════════════ */
        .wcu-hero {
          position: relative;
          min-height: 92vh;
          display: flex;
          align-items: center;
          overflow: hidden;
        }
        .wcu-hero-bg {
          position: absolute;
          inset: 0;
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
        }
        .wcu-hero-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            135deg,
            rgba(1, 68, 149, 0.93) 0%,
            rgba(43, 88, 143, 0.82) 55%,
            rgba(144, 188, 242, 0.55) 100%
          );
        }
        .wcu-hero-grid {
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px);
          background-size: 60px 60px;
        }
        .wcu-hero-inner {
          position: relative;
          z-index: 2;
          max-width: 1280px;
          margin: 0 auto;
          width: 100%;
          padding: 0 48px 80px;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 80px;
          align-items: center;
        }
        .wcu-hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          background: rgba(255,255,255,0.10);
          border: 1px solid rgba(255,255,255,0.22);
          color: rgba(255,255,255,0.90);
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.18em;
          text-transform: uppercase;
          padding: 8px 18px;
          border-radius: 100px;
          margin-bottom: 28px;
          opacity: 0;
          transform: translateY(16px);
          transition: opacity 0.7s ease, transform 0.7s ease;
        }
        .wcu-hero-badge::before {
          content: '';
          width: 6px; height: 6px;
          border-radius: 50%;
          background: var(--white);
          animation: heroPulse 2s ease-in-out infinite;
          flex-shrink: 0;
        }
        @keyframes heroPulse {
          0%,100% { opacity: 1; transform: scale(1); }
          50%      { opacity: 0.4; transform: scale(1.5); }
        }
        .wcu-hero-heading {
          font-family: 'Playfair Display', serif;
          font-size: clamp(30px, 3.2vw, 64px);
          font-weight: 700;
          color: #ffffff;
          line-height: 1.25;
          margin: 0 0 28px;
          opacity: 0;
          transform: translateY(24px);
          transition: opacity 0.8s ease 0.1s, transform 0.8s ease 0.1s;
        }
        .wcu-hero-heading em {
          font-style: italic;
          color: var(--gray);
        }
        .wcu-hero-sub {
          font-size: 16px;
          font-weight: 300;
          text-align: justify;
          line-height: 1.75;
          color: rgb(255, 255, 255);
          max-width: 520px;
          margin: 0 0 44px;
          opacity: 0;
          transform: translateY(24px);
          transition: opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s;
        }
        .wcu-hero-cta {
          display: flex;
          gap: 16px;
          flex-wrap: wrap;
          opacity: 0;
          transform: translateY(24px);
          transition: opacity 0.8s ease 0.3s, transform 0.8s ease 0.3s;
        }
        .wcu-btn-primary {
          background: var(--white);
          color: var(--blue);
          font-family: var(--sans);
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          padding: 14px 32px;
          border-radius: 8px;
          cursor: pointer;
          text-decoration: none;
          display: inline-block;
          transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s;
        }
        .wcu-btn-primary:hover {
          background: var(--light);
          transform: translateY(-2px);
          box-shadow: 0 12px 32px rgba(1,42,93,0.30);
        }
        .wcu-btn-outline-white {
          background: transparent;
          color: rgba(255,255,255,0.85);
          font-family: var(--sans);
          font-size: 12px;
          font-weight: 500;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          padding: 14px 32px;
          border: 1px solid rgba(255,255,255,0.35);
          border-radius: 8px;
          cursor: pointer;
          text-decoration: none;
          display: inline-block;
          transition: border-color 0.2s, background 0.2s, color 0.2s;
        }
        .wcu-btn-outline-white:hover {
          border-color: var(--white);
          background: rgba(255,255,255,0.10);
          color: var(--white);
        }
        /* hero right stats card */
        .wcu-hero-visual {
          position: relative;
          opacity: 0;
          transform: translateX(32px);
          transition: opacity 1s ease 0.4s, transform 1s ease 0.4s;
        }
        .wcu-hero-visual-card {
          background: rgba(255,255,255,0.07);
          border: 1px solid rgba(255,255,255,0.14);
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
          border-radius: var(--radius-lg);
          padding: 40px;
          position: relative;
        }
        .wcu-hero-visual-card::before {
          content: '';
          position: absolute;
          top: -1px; left: -1px; right: -1px;
          height: 2px;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.45), transparent);
          border-radius: var(--radius-lg) var(--radius-lg) 0 0;
        }
        .wcu-hero-stats-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
          margin-bottom: 32px;
        }
        .wcu-hero-mini-stat {
          text-align: center;
          padding: 20px 16px;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: var(--radius);
        }
        .wcu-hero-mini-num {
          font-family: var(--serif);
          font-size: 38px;
          font-weight: 1000;
          color: var(--white);
          line-height: 1;
          margin-bottom: 6px;
        }
        .wcu-hero-mini-label {
          font-size: 11px;
          font-weight: 600;
          color: rgb(255, 255, 255);
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }
        .wcu-hero-quote {
          font-family: var(--serif);
          font-size: 17px;
          font-style: italic;
          color: rgb(255, 255, 255);
          line-height: 1.6;
          border-left: 2px solid rgba(255,255,255,0.30);
          padding-left: 20px;
          margin: 0;
        }
        /* animate-in */
        .wcu-hero.is-visible .wcu-hero-badge,
        .wcu-hero.is-visible .wcu-hero-heading,
        .wcu-hero.is-visible .wcu-hero-sub,
        .wcu-hero.is-visible .wcu-hero-cta,
        .wcu-hero.is-visible .wcu-hero-visual {
          opacity: 1;
          transform: none;
        }
        /* scroll hint */
        .wcu-scroll-hint {
          position: absolute;
          bottom: 40px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
          opacity: 0.40;
          z-index: 3;
        }
        .wcu-scroll-hint span {
          font-size: 10px;
          letter-spacing: 0.15em;
          color: var(--white);
          text-transform: uppercase;
        }
        .wcu-scroll-line {
          width: 1px;
          height: 40px;
          background: linear-gradient(to bottom, var(--white), transparent);
          animation: scrollDrop 1.8s ease-in-out infinite;
        }
        @keyframes scrollDrop {
          0%,100% { transform: scaleY(1); opacity: 1; }
          50%      { transform: scaleY(0.4); opacity: 0.3; }
        }

        /* ════════════════════════════════════════
           OUR STORY
        ════════════════════════════════════════ */
        .wcu-story {
          padding: 50px 48px;
          max-width: 1280px;
          margin: 0 auto;
        }
        .wcu-story-inner {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 100px;
          align-items: center;
        }
        .wcu-section-eyebrow {
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: var(--blue);
          margin-bottom: 20px;
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .wcu-section-eyebrow::before {
          content: '';
          width: 30px;
          height: 1px;
          background: var(--blue);
          flex-shrink: 0;
        }
        .wcu-story h2 {
          font-family: var(--serif);
          font-size: clamp(36px, 4vw, 56px);
          font-weight: 400;
          line-height: 1.18;
          color: var(--dark);
          margin: 0 0 32px;
        }
        .wcu-story h2 em { font-style: italic; color: var(--blue); }
        .wcu-story p {
          font-size: 15px;
          line-height: 1.85;
          color: var(--text-mid);
          margin: 0 0 20px;
          text-align: justify;
        }
        .wcu-story-image-wrap { position: relative; }
        .wcu-story-img-frame {
          position: relative;
          border-radius: var(--radius-lg);
          overflow: hidden;
          aspect-ratio: 4/5;
        }
        .wcu-story-img-frame img {
          width: 100%; height: 100%;
          object-fit: cover;
          display: block;
        }
        .wcu-story-img-frame::after {
          content: '';
          position: absolute;
          inset: 0;
        }
        .wcu-story-float-badge {
          position: absolute;
          bottom: -24px;
          right: -24px;
          background: var(--blue);
          border: 1px solid rgba(255,255,255,0.12);
          border-radius: var(--radius-lg);
          padding: 28px 32px;
          min-width: 200px;
          z-index: 2;
        }
        .wcu-story-float-num {
          font-family: var(--serif);
          font-size: 48px;
          font-weight: 500;
          color: var(--white);
          line-height: 1;
        }
        .wcu-story-float-label {
          font-size: 13px;
          color: rgba(255,255,255,0.65);
          margin-top: 6px;
          line-height: 1.4;
        }
        .wcu-story-decor {
          position: absolute;
          top: -20px; left: -20px;
          width: 120px; height: 120px;
          border: 1px solid rgba(1,42,93,0.15);
          border-radius: var(--radius-lg);
          z-index: -1;
        }

        /* ════════════════════════════════════════
           FADE HELPERS
        ════════════════════════════════════════ */
        .wcu-fade {
          opacity: 0;
          transform: translateY(32px);
          transition: opacity 0.8s ease, transform 0.8s ease;
        }
        .wcu-fade.is-visible { opacity: 1; transform: none; }
        .wcu-fade-left {
          opacity: 0;
          transform: translateX(-32px);
          transition: opacity 0.8s ease, transform 0.8s ease;
        }
        .wcu-fade-left.is-visible { opacity: 1; transform: none; }
        .wcu-fade-right {
          opacity: 0;
          transform: translateX(32px);
          transition: opacity 0.9s ease 0.1s, transform 0.9s ease 0.1s;
        }
        .wcu-fade-right.is-visible { opacity: 1; transform: none; }

        /* ════════════════════════════════════════
           STATS BAND
        ════════════════════════════════════════ */
        .wcu-stats-band {
          background: var(--blue);
          padding: 80px 48px;
          position: relative;
          overflow: hidden;
        }
        .wcu-stats-band::before {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.22), transparent);
        }
        .wcu-stats-band::after {
          content: '';
          position: absolute;
          bottom: 0; left: 0; right: 0;
          height: 1px;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.22), transparent);
        }
        .wcu-stats-band-glow {
          position: absolute;
          inset: 0;
          background: radial-gradient(ellipse 70% 120% at 50% 50%, rgba(255,255,255,0.04) 0%, transparent 70%);
          pointer-events: none;
        }
        .wcu-stats-inner {
          max-width: 1280px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
        }
        .wcu-stats-label {
          text-align: center;
          font-family: var(--serif);
          font-size: 22px;
          font-weight: 300;
          color: rgba(255,255,255,0.40);
          margin-bottom: 56px;
          font-style: italic;
          letter-spacing: 0.02em;
        }
        .wcu-stats-grid {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 0;
        }
        .wcu-stat-card {
          text-align: center;
          padding: 32px 24px;
          position: relative;
          opacity: 0;
          transform: translateY(20px);
          transition: opacity 0.6s ease, transform 0.6s ease;
        }
        .wcu-stat-card.is-visible { opacity: 1; transform: none; }
        .wcu-stat-card + .wcu-stat-card::before {
          content: '';
          position: absolute;
          left: 0; top: 20%; bottom: 20%;
          width: 1px;
          background: rgba(255,255,255,0.12);
        }
        .wcu-stat-number {
          display: flex;
          align-items: baseline;
          justify-content: center;
          gap: 2px;
          margin-bottom: 10px;
        }
        .wcu-stat-count {
          font-family: var(--serif);
          font-size: 52px;
          font-weight: 500;
          color: var(--white);
          line-height: 1;
        }
        .wcu-stat-suffix {
          font-family: var(--serif);
          font-size: 30px;
          font-weight: 400;
          color: rgba(255,255,255,0.65);
        }
        .wcu-stat-label {
          font-size: 12px;
          color: rgba(255,255,255,0.48);
          letter-spacing: 0.1em;
          text-transform: uppercase;
          line-height: 1.5;
          margin: 0;
        }

        /* ════════════════════════════════════════
           VALUES  — FLIP CARD DESIGN
        ════════════════════════════════════════ */
        .wcu-values {
          padding: 80px 48px;
          background: var(--light);
          position: relative;
          overflow: hidden;
        }
        
        .wcu-values-inner {
          max-width: 1280px;
          margin: 0 auto;
        }
        .wcu-values-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  max-width: 720px;
  margin: 0 auto 80px;
}
        .wcu-values-header h2 {
          font-family: var(--serif);
          font-size: clamp(36px, 4vw, 56px);
          font-weight: 400;
          line-height: 1.15;
          color: var(--dark);
          margin: 0;
        }
        .wcu-values-header h2 em { font-style: italic; color: var(--blue); align-self: center; }
        .wcu-values-header p {
          padding-top: 20px;
          font-size: 15px;
          text-align: justify;
          line-height: 1.8;
          color: var(--text-mid);
          margin: 0;
        }
        .wcu-values-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 20px;
        }
        .wcu-values-grid-row2 {
  grid-template-columns: repeat(3, 1fr);
  max-width: 75%; /* matches 3 of 4 columns */
  margin-left: auto;
  margin-right: auto;
}
        /* ── Flip card wrapper ── */
        .wcu-value-card {
          perspective: 1000px;
          height: 200px;
          cursor: default;
          opacity: 0;
          transform: translateY(32px);
          transition: opacity 0.8s ease, transform 0.8s ease;
        }
        .wcu-value-card.is-visible {
          opacity: 1;
          transform: translateY(0);
        }

        /* ── Inner flipper ── */
        .wcu-flip-inner {
          position: relative;
          width: 100%;
          height: 100%;
          transform-style: preserve-3d;
          transition: transform 0.65s cubic-bezier(0.4, 0.2, 0.2, 1);
          border-radius: 14px;
        }
        .wcu-value-card:hover .wcu-flip-inner {
          transform: rotateY(180deg);
        }

        /* ── Shared face styles ── */
        .wcu-flip-front,
        .wcu-flip-back {
          position: absolute;
          inset: 0;
          border-radius: 14px;
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 36px 28px;
          overflow: hidden;
        }

        /* ── FRONT face ── */
        .wcu-flip-front {
          background: rgba(255,255,255,0.85);
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border: 1px solid rgba(1,42,93,0.08);
          gap: 20px;
          text-align: center;
        }
        .wcu-flip-front::after {
          content: '';
          position: absolute;
          top: 0; left: 0; right: 0;
          height: 3px;
          background: linear-gradient(90deg, transparent, rgba(1,66,147,0.35), transparent);
          border-radius: 14px 14px 0 0;
          opacity: 0;
          transition: opacity 0.35s ease;
        }
        .wcu-value-card:hover .wcu-flip-front::after {
          opacity: 1;
        }
        .wcu-flip-icon {
          width: 60px;
          height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 14px;
          background: linear-gradient(135deg, rgba(1,66,147,0.12), rgba(1,66,147,0.03));
          color: var(--blue);
          flex-shrink: 0;
        }
        .wcu-flip-icon svg {
          width: 28px;
          height: 28px;
        }
        .wcu-flip-title {
          font-family: var(--serif);
          font-size: 22px;
          font-weight: 500;
          color: var(--dark);
          margin: 0;
          line-height: 1.2;
        }
        /* subtle hover-hint label */
        .wcu-flip-hint {
          font-size: 10px;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: rgba(1,66,147,0.40);
          margin: 0;
          margin-top: -4px;
        }

        /* ── BACK face ── */
        .wcu-flip-back {
          background: var(--blue);
          border: 1px solid rgba(1,66,147,0.15);
          transform: rotateY(180deg);
          gap: 16px;
          text-align: center;
        }
        .wcu-flip-back::before {
          content: '';
          position: absolute;
          inset: 0;
          background: radial-gradient(ellipse 80% 80% at 50% 20%, rgba(255,255,255,0.07) 0%, transparent 70%);
          border-radius: inherit;
          pointer-events: none;
        }
        .wcu-flip-back-icon {
          width: 44px;
          height: 44px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 10px;
          background: rgba(255,255,255,0.12);
          color: rgba(255,255,255,0.80);
          flex-shrink: 0;
        }
        .wcu-flip-back-icon svg {
          width: 22px;
          height: 22px;
        }
        .wcu-flip-back-title {
          font-family: var(--serif);
          font-size: 19px;
          font-weight: 500;
          color: var(--white);
          margin: 0;
          line-height: 1.2;
        }
        .wcu-flip-back-text {
          font-size: 13px;
          line-height: 1.70;
          color: rgba(255,255,255,0.72);
          margin: 0;
        }

        /* ════════════════════════════════════════
           CTA BAND
        ════════════════════════════════════════ */
        .wcu-cta {
          position: relative;
          padding: 100px 48px;
          text-align: center;
          overflow: hidden;
        }
        .wcu-cta-bg {
          position: absolute;
          inset: 0;
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
        }
        .wcu-cta-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            135deg,
            rgba(1, 42, 93, 0.94) 0%,
            rgba(1, 48, 110, 0.85) 100%
          );
        }
        .wcu-cta-inner {
          position: relative;
          z-index: 2;
          max-width: 700px;
          margin: 0 auto;
        }
        .wcu-cta h2 {
          font-family: var(--serif);
          font-size: clamp(32px, 4vw, 52px);
          font-weight: 400;
          color: var(--white);
          margin: 0 0 16px;
          line-height: 1.2;
        }
        .wcu-cta p {
          font-size: 15px;
          color: rgba(255,255,255,0.68);
          margin: 0 0 36px;
          line-height: 1.7;
        }
        .wcu-btn-cta {
          background: var(--white);
          color: var(--blue);
          font-family: var(--sans);
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          padding: 16px 44px;
          border: none;
          cursor: pointer;
          text-decoration: none;
          display: inline-block;
          transition: transform 0.2s, box-shadow 0.2s, background 0.2s;
        }
        .wcu-btn-cta:hover {
          background: var(--light);
          transform: translateY(-2px);
          box-shadow: 0 12px 32px rgba(0,0,0,0.25);
        }

        /* ════════════════════════════════════════
           RESPONSIVE
        ════════════════════════════════════════ */
        @media (max-width: 1024px) {
          .wcu-hero-inner,
          .wcu-story-inner,
          .wcu-values-header { grid-template-columns: 1fr; gap: 48px; }
          .wcu-hero-visual { display: none; }
          .wcu-values-grid { grid-template-columns: repeat(2, 1fr); }
          .wcu-stats-grid { grid-template-columns: repeat(3, 1fr); }
          .wcu-story-float-badge { right: 16px; bottom: 16px; }
        }
        @media (max-width: 768px) {
          .wcu-hero-inner,
          .wcu-story,
          .wcu-values,
          .wcu-stats-band,
          .wcu-cta { padding-left: 24px; padding-right: 24px; }
          .wcu-values-grid { grid-template-columns: 1fr; }
          .wcu-stats-grid { grid-template-columns: repeat(2, 1fr); }
          /* On touch devices, tap to flip */
          .wcu-value-card:active .wcu-flip-inner { transform: rotateY(180deg); }
        }

        @media (max-width: 1024px) {
  .wcu-values-grid-row2 { max-width: 100%; }
}
  
      `}</style>

      <div className="wcu-root">
        {/* ══ HERO ══ */}
        <section
          className={`wcu-hero${heroInView ? " is-visible" : ""}`}
          ref={heroRef}
          id="why-choose-us"
        >
          <div
            className="wcu-hero-bg"
            style={{
              backgroundImage: `url(${config.imageBaseURL}${config.paths.banners}uae-cfobridge5.png)`,
            }}
          />
          <div className="wcu-hero-overlay" />
          <div className="wcu-hero-grid" />

          <div className="wcu-hero-inner">
            <div className="wcu-hero-content">
              <div className="wcu-hero-badge">
                UAE &amp; GCC Financial Leadership
              </div>
              <h1 className="wcu-hero-heading">
                Trusted CFO Consultants &amp; <em>Outsourced CFO</em> Services
                in UAE
              </h1>
              <p className="wcu-hero-sub">
                CFO Bridge UAE delivers 50+ years of combined CFO Partner
                expertise to the UAE market, empowering startups across Middle
                East, North Africa (MENA) and Asia with high-impact financial
                leadership — without the need for a full-time CFO.
              </p>
              <div className="wcu-hero-cta">
                <a
                  href="#contact"
                  className="wcu-btn-primary"
                  onClick={handleContactClick}
                >
                  Schedule Consultation
                </a>
                <a href="/services" className="wcu-btn-outline-white">
                  Explore Services
                </a>
              </div>
            </div>

            <div className="wcu-hero-visual">
              <div className="wcu-hero-visual-card">
                <div className="wcu-hero-stats-grid">
                  <div className="wcu-hero-mini-stat">
                    <div className="wcu-hero-mini-num">550+</div>
                    <div className="wcu-hero-mini-label">
                      Engagements Globally
                    </div>
                  </div>
                  <div className="wcu-hero-mini-stat">
                    <div className="wcu-hero-mini-num">3</div>
                    <div className="wcu-hero-mini-label">Countries Served</div>
                  </div>
                  <div className="wcu-hero-mini-stat">
                    <div className="wcu-hero-mini-num">13+</div>
                    <div className="wcu-hero-mini-label">
                      Years of Excellence
                    </div>
                  </div>
                  <div className="wcu-hero-mini-stat">
                    <div className="wcu-hero-mini-num">40+</div>
                    <div className="wcu-hero-mini-label">
                      Expert CFO Professionals
                    </div>
                  </div>
                </div>
                <p className="wcu-hero-quote">
                  "Transforming finance into a true growth engine for UAE's most
                  ambitious businesses."
                </p>
              </div>
            </div>
          </div>

          <div className="wcu-scroll-hint">
            <span>Scroll</span>
            <div className="wcu-scroll-line" />
          </div>
        </section>

        {/* ══ OUR STORY ══ */}
        <section className="wcu-story" ref={storyRef} id="our-story">
          <div className="wcu-story-inner">
            <div className={`wcu-fade-left${storyInView ? " is-visible" : ""}`}>
              <div className="wcu-story-image-wrap">
                <div className="wcu-story-decor" />
                <div className="wcu-story-img-frame">
                  <img
                    src={`${config.imageBaseURL}${config.paths.images}why_cfo.webp`}
                    alt="CFO Bridge UAE — Professional Financial Services"
                  />
                </div>
              </div>
            </div>

            <div
              className={`wcu-fade-right${storyInView ? " is-visible" : ""}`}
            >
              <h2>
                <em>Our Story</em>
              </h2>
              <p>
                CFO Bridge was founded in India in 2012, beginning its journey
                with a clear vision to deliver practical and high-impact
                financial leadership. Over the years, we have partnered with
                startups, SMEs, and growth-stage companies, building strong
                financial foundations and helping them make strategic decisions.
              </p>
              <p>
                In 2025, CFO Bridge entered the UAE market, applying its
                execution-focused expertise to help businesses across the MENA
                region strengthen financial clarity, control, and operational
                consistency.
              </p>
              <p>
                Our UAE offering is designed to address critical business needs
                with precision. It includes Virtual and Interim CFO Services,
                E-Invoicing Setup and Compliance, Company Setup and Winding
                Down, Working Capital Management, M&A and Fundraising, IPO
                Readiness, ERP Implementation, and AML, Corporate Tax, and VAT
                advisory.
              </p>
              <p>
                We partner with businesses to build strong financial foundations
                that support confident and scalable growth.
              </p>
            </div>
          </div>
        </section>

        {/* ══ OUR VALUES ══ */}
        <section className="wcu-values" ref={valuesRef} id="our-values">
          <div className="wcu-values-inner">
            <div
              className={`wcu-values-header wcu-fade${valuesInView ? " is-visible" : ""}`}
            >
              <h2>
                <em>Our Values</em>
              </h2>
              <p>
                At CFO Bridge UAE, our values are not aspirational posters —
                they are the operational code our consultants live by on every
                client engagement, every decision, and every conversation across
                the GCC.
              </p>
            </div>

            {/* Row 1 — 4 cards */}
            <div className="wcu-values-grid">
              {values.slice(0, 4).map((v, i) => (
                <div
                  key={i}
                  className={`wcu-value-card${valuesInView ? " is-visible" : ""}`}
                  style={{ transitionDelay: `${i * 80}ms` }}
                >
                  <div className="wcu-flip-inner">
                    <div className="wcu-flip-front">
                      <div className="wcu-flip-icon">{v.icon}</div>
                      <h3 className="wcu-flip-title">{v.title}</h3>
                      <p className="wcu-flip-hint">Hover to learn more</p>
                    </div>
                    <div className="wcu-flip-back">
                      <h3 className="wcu-flip-back-title">{v.title}</h3>
                      <p className="wcu-flip-back-text">{v.text}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Row 2 — 3 cards, centered */}
            <div
              className="wcu-values-grid wcu-values-grid-row2"
              style={{ marginTop: "20px" }}
            >
              {values.slice(4).map((v, i) => (
                <div
                  key={i + 4}
                  className={`wcu-value-card${valuesInView ? " is-visible" : ""}`}
                  style={{ transitionDelay: `${(i + 4) * 80}ms` }}
                >
                  <div className="wcu-flip-inner">
                    <div className="wcu-flip-front">
                      <div className="wcu-flip-icon">{v.icon}</div>
                      <h3 className="wcu-flip-title">{v.title}</h3>
                      <p className="wcu-flip-hint">Hover to learn more</p>
                    </div>
                    <div className="wcu-flip-back">
                      <h3 className="wcu-flip-back-title">{v.title}</h3>
                      <p className="wcu-flip-back-text">{v.text}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ══ FAQ ══ 
        <FAQ
          faqs={aboutFaqSchema.mainEntity.map((q) => ({
            heading: q.name,
            description: q.acceptedAnswer.text,
          }))}
        />*/}

        {/* ══ CTA BAND ══ */}
        <section className="wcu-cta">
          <div
            className="wcu-cta-bg"
            style={{
              backgroundImage: `url(${config.imageBaseURL}${config.paths.banners}uae-cfobridge2.webp)`,
            }}
          />
          <div className="wcu-cta-overlay" />
          <div className="wcu-cta-inner">
            <h2>Ready to elevate your finance function?</h2>
            <p>
              Let CFO Bridge UAE become the strategic financial partner your
              business deserves — on-demand expertise, real ROI, zero full-time
              overhead.
            </p>
            <a
              href="#contact"
              className="wcu-btn-cta"
              onClick={handleContactClick}
            >
              Schedule Consultation
            </a>
          </div>
        </section>
      </div>
    </>
  );
};

export default WhyChooseUs;
